import {
  AutomatedNameUpdateStatus,
  type WorkerRequirementIncludedAutomatedNameUpdateType,
} from "@clipboard-health/contract-worker-app-bff";
import { type UseModalState } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";

import { OnboardingPersonalIdVerificationApprovedBottomSheet } from "./ApprovedBottomSheet";
import { PersonalIdVerificationApprovedWithFlaggedNameBottomSheet } from "./ApprovedWithFlaggedNameBottomSheet";
import { PersonalIdVerificationApprovedWithNameUpdateBottomSheet } from "./ApprovedWithNameUpdateBottomSheet";
import { type OnboardingPersonalIdVerificationAnalytics } from "./types";
import { getNamesFromAutomatedNameUpdate } from "./utils";

interface OnboardingPersonalIdVerificationApprovedBottomSheetContainerProps {
  modalState: UseModalState;
  isLoading: boolean;
  onContinue: () => void;
  automatedNameUpdate?: WorkerRequirementIncludedAutomatedNameUpdateType;
  analytics: OnboardingPersonalIdVerificationAnalytics;
}

export function OnboardingPersonalIdVerificationApprovedBottomSheetContainer(
  props: OnboardingPersonalIdVerificationApprovedBottomSheetContainerProps
) {
  const { modalState, isLoading, onContinue, automatedNameUpdate, analytics } = props;

  const isAutomatedNameUpdate = isDefined(automatedNameUpdate);
  const isPendingAutomatedNameUpdate =
    isAutomatedNameUpdate &&
    automatedNameUpdate.attributes.status === AutomatedNameUpdateStatus.PENDING;
  const isFlaggedAutomatedNameUpdate =
    isAutomatedNameUpdate &&
    automatedNameUpdate.attributes.status === AutomatedNameUpdateStatus.FLAGGED;

  const commonProps = {
    canDismiss: false,
    modalState,
    paperSx: {
      ...getSafeAreaPaddings({ bottom: true }),
    },
    analytics,
  };

  if (isPendingAutomatedNameUpdate) {
    return (
      <PersonalIdVerificationApprovedWithNameUpdateBottomSheet
        {...commonProps}
        {...getNamesFromAutomatedNameUpdate(automatedNameUpdate)}
        automatedNameUpdateId={automatedNameUpdate.id}
      />
    );
  }

  if (isFlaggedAutomatedNameUpdate) {
    return (
      <PersonalIdVerificationApprovedWithFlaggedNameBottomSheet
        {...commonProps}
        isLoading={isLoading}
        onContinue={onContinue}
      />
    );
  }

  // In case we don't have an automated name update or it's acknowledged without flagging, show the normal approved bottom sheet.
  return (
    <OnboardingPersonalIdVerificationApprovedBottomSheet
      {...commonProps}
      isLoading={isLoading}
      onContinue={onContinue}
    />
  );
}
